import Papa from 'papaparse';

interface PostOfficeData {
  officename: string;
  pincode: string;
  officeType: string;
  Deliverystatus: string;
  divisionname: string;
  regionname: string;
  circlename: string;
  Taluk: string;
  Districtname: string;
  statename: string;
}

interface AreaPincodeMapping {
  [pincode: string]: {
    areas: string[];
    district: string;
    state: string;
  };
}

let postOfficeData: PostOfficeData[] | null = null;
let areaPincodeMapping: AreaPincodeMapping = {};

const loadAreaPincodeMapping = async (): Promise<AreaPincodeMapping> => {
  if (Object.keys(areaPincodeMapping).length > 0) return areaPincodeMapping;

  try {
    const response = await fetch('/data/area_pincode_mapping.json');
    areaPincodeMapping = await response.json();
    return areaPincodeMapping;
  } catch (error) {
    console.error('Error loading area-pincode mapping:', error);
    return {};
  }
};

export const loadCSVData = async (): Promise<PostOfficeData[]> => {
  if (postOfficeData) return postOfficeData;

  try {
    const response = await fetch('/data/All pincode data.csv');
    const csvText = await response.text();
    
    const { data } = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true
    });

    postOfficeData = data as PostOfficeData[];
    return postOfficeData;
  } catch (error) {
    console.error('Error loading CSV:', error);
    return [];
  }
};

const findAreaInMapping = async (searchTerm: string): Promise<PostOfficeData[]> => {
  const mapping = await loadAreaPincodeMapping();
  const term = searchTerm.toLowerCase().trim();
  
  for (const [pincode, data] of Object.entries(mapping)) {
    if (data.areas.some(area => area.toLowerCase().includes(term))) {
      const postOffices = await loadCSVData();
      const matchingOffices = postOffices.filter(po => po.pincode === pincode);
      
      if (matchingOffices.length > 0) {
        return matchingOffices;
      } else {
        return [{
          officename: `${term.charAt(0).toUpperCase() + term.slice(1)}`,
          pincode: pincode,
          officeType: 'Area',
          Deliverystatus: 'Delivery',
          divisionname: data.district,
          regionname: data.state,
          circlename: data.state,
          Taluk: data.district,
          Districtname: data.district,
          statename: data.state
        }];
      }
    }
  }
  
  return [];
};

export const searchPostOffices = async (searchTerm: string): Promise<PostOfficeData[]> => {
  const data = await loadCSVData();
  const term = searchTerm.toLowerCase().trim();

  if (/^\d{6}$/.test(term)) {
    const postOfficeResults = data.filter(po => po.pincode === term);
    const mapping = await loadAreaPincodeMapping();
    
    if (mapping[term]) {
      const areas = mapping[term].areas;
      if (postOfficeResults.length > 0) {
        const firstOffice = postOfficeResults[0];
        return [{
          ...firstOffice,
          officeType: `${firstOffice.officeType} (Serves: ${areas.join(', ')})`
        }, ...postOfficeResults.slice(1)];
      }
    }
    
    return postOfficeResults;
  }

  const postOfficeResults = data.filter(po => {
    const officeName = po.officename.toLowerCase();
    const district = po.Districtname.toLowerCase();
    const state = po.statename.toLowerCase();
    
    return officeName.includes(term) || 
           district.includes(term) || 
           state.includes(term);
  });

  const areaResults = await findAreaInMapping(term);
  const allResults = [...postOfficeResults, ...areaResults];
  const uniqueResults = allResults.filter((po, index, self) =>
    index === self.findIndex((t) => t.pincode === po.pincode)
  );

  return uniqueResults;
};
